import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import { Table } from "@tiptap/extension-table"
import TableRow from "@tiptap/extension-table-row"
import TableHeader from "@tiptap/extension-table-header"
import TableCell from "@tiptap/extension-table-cell"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import Placeholder from "@tiptap/extension-placeholder"
import CharacterCount from "@tiptap/extension-character-count"
import TextAlign from "@tiptap/extension-text-align"
import Highlight from "@tiptap/extension-highlight"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import Typography from "@tiptap/extension-typography"
import Color from "@tiptap/extension-color"
import { TextStyle } from "@tiptap/extension-text-style"
import Youtube from "@tiptap/extension-youtube"
import { createLowlight, common } from "lowlight"

const lowlight = createLowlight(common)

type Hook = {
  el: HTMLElement
  pushEvent: (event: string, payload: object, callback?: Function) => void
  handleEvent: (event: string, callback: (payload: any) => void) => void
  _editor: Editor | null
}

export const TipTap = {
  _editor: null as Editor | null,

  mounted(this: Hook) {
    const wrapper = this.el
    const editorEl = wrapper.querySelector<HTMLElement>("#tiptap-editor-content")

    if (!editorEl) {
      console.error("TipTap: #tiptap-editor-content not found")
      return
    }

    let initialContent: object = {}
    try {
      const raw = wrapper.dataset.content || "{}"
      initialContent = JSON.parse(raw)
    } catch {
      initialContent = {}
    }

    const editor = new Editor({
      element: editorEl,
      extensions: [
        StarterKit.configure({
          codeBlock: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        Image.configure({
          inline: false,
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        CodeBlockLowlight.configure({
          lowlight,
        }),
        Placeholder.configure({
          placeholder: "Start writing your blog post...",
        }),
        CharacterCount,
        TextAlign.configure({
          types: ["paragraph", "heading"],
        }),
        Highlight.configure({
          multicolor: true,
        }),
        TaskList,
        TaskItem.configure({
          nested: true,
        }),
        Typography,
        Color,
        TextStyle,
        Youtube.configure({
          controls: true,
          nocookie: true,
        }),
      ],
      content: Object.keys(initialContent).length > 0 ? initialContent : "",
      onUpdate: ({ editor }) => {
        this.pushEvent("content_updated", {
          json: JSON.stringify(editor.getJSON()),
          html: editor.getHTML(),
          wordCount: editor.storage.characterCount.words(),
        })
      },
    })

    ;(this as any)._editor = editor

    this.handleEvent("set_content", ({ content }: { content: string }) => {
      try {
        const parsed = JSON.parse(content)
        editor.commands.setContent(parsed)
      } catch {
        editor.commands.setContent(content)
      }
    })

    this.handleEvent("exec_command", ({ command, attrs }: { command: string; attrs?: any }) => {
      const chain = editor.chain().focus()
      switch (command) {
        case "bold":
          chain.toggleBold().run()
          break
        case "italic":
          chain.toggleItalic().run()
          break
        case "underline":
          chain.toggleUnderline().run()
          break
        case "strike":
          chain.toggleStrike().run()
          break
        case "h1":
          chain.toggleHeading({ level: 1 }).run()
          break
        case "h2":
          chain.toggleHeading({ level: 2 }).run()
          break
        case "h3":
          chain.toggleHeading({ level: 3 }).run()
          break
        case "bulletList":
          chain.toggleBulletList().run()
          break
        case "orderedList":
          chain.toggleOrderedList().run()
          break
        case "taskList":
          chain.toggleTaskList().run()
          break
        case "blockquote":
          chain.toggleBlockquote().run()
          break
        case "codeBlock":
          chain.toggleCodeBlock().run()
          break
        case "horizontalRule":
          chain.setHorizontalRule().run()
          break
        case "alignLeft":
          chain.setTextAlign("left").run()
          break
        case "alignCenter":
          chain.setTextAlign("center").run()
          break
        case "alignRight":
          chain.setTextAlign("right").run()
          break
        case "highlightYellow":
          chain.toggleHighlight({ color: "#fde68a" }).run()
          break
        case "highlightPink":
          chain.toggleHighlight({ color: "#fbcfe8" }).run()
          break
        case "highlightGreen":
          chain.toggleHighlight({ color: "#bbf7d0" }).run()
          break
        case "insertTable":
          chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          break
        case "addYoutube":
          if (attrs?.url) {
            chain.setYoutubeVideo({ src: attrs.url }).run()
          }
          break
        case "addImage":
          if (attrs?.url) {
            chain.setImage({ src: attrs.url }).run()
          }
          break
        case "undo":
          chain.undo().run()
          break
        case "redo":
          chain.redo().run()
          break
        default:
          console.warn("TipTap: unknown command", command)
      }
    })
  },

  destroyed(this: Hook) {
    if ((this as any)._editor) {
      ;(this as any)._editor.destroy()
      ;(this as any)._editor = null
    }
  },
}
